import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPaymentStatus } from "../lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [amount, setAmount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      try {
        const data = await getPaymentStatus(sessionId);
        if (cancelled) return;
        setAmount(data.amount);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
        tries += 1;
        if (tries < 20) setTimeout(poll, 2000);
        else setStatus("pending");
      } catch {
        setStatus("failed");
      }
    };
    if (sessionId) poll();
    else setStatus("failed");
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <section className="bg-[#FDFBF7]">
      <div className="container-x py-24 md:py-32 max-w-2xl text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-[#6B21A8]" />
            <h1 className="mt-6 font-display text-4xl font-bold">Confirming your gift…</h1>
            <p className="mt-3 text-black/60">This usually takes just a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-16 w-16 text-[#6B21A8]" data-testid="payment-success-icon" />
            <h1 className="mt-6 font-display text-5xl font-bold">Thank you.</h1>
            <p className="mt-4 text-black/70 text-lg">Your gift of ${amount} just helped change a young man's life.</p>
            <p className="mt-2 text-black/60">A receipt will be emailed to you shortly.</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/success-stories" className="btn-primary" data-testid="success-see-stories">See who you're funding</Link>
              <Link to="/" className="btn-outline">Back home</Link>
            </div>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-[#6B21A8]" />
            <h1 className="mt-6 font-display text-4xl font-bold">Still processing…</h1>
            <p className="mt-3 text-black/60">Your gift is pending confirmation. You'll get an email when it's complete.</p>
            <Link to="/" className="mt-8 inline-block btn-outline">Back home</Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="mx-auto h-16 w-16 text-red-600" />
            <h1 className="mt-6 font-display text-4xl font-bold">We couldn't confirm your gift.</h1>
            <p className="mt-3 text-black/70">If you were charged, please contact us — no worries, we'll sort it out.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/donate" className="btn-primary">Try again</Link>
              <Link to="/contact" className="btn-outline">Contact us</Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export function PaymentCancel() {
  return (
    <section className="bg-[#FDFBF7]">
      <div className="container-x py-24 md:py-32 max-w-2xl text-center">
        <h1 className="font-display text-5xl font-bold">Checkout canceled.</h1>
        <p className="mt-4 text-black/70">No worries — your gift can happen anytime you're ready.</p>
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link to="/donate" className="btn-primary" data-testid="cancel-try-again">Try again</Link>
          <Link to="/" className="btn-outline">Back home</Link>
        </div>
      </div>
    </section>
  );
}
